import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

// Debounce delay for realtime events (ms)
const DEBOUNCE_DELAY = 500;

export const ChatBadge = () => {
  const { userProfile } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  // Refs for debouncing and cleanup
  const debounceRef = useRef(null);
  const mountedRef = useRef(true);

  /**
   * Fetch total unread count using optimized RPC
   * Single aggregated query instead of fetching all rows
   */
  const fetchUnreadCount = useCallback(async () => {
    if (!userProfile?.id) return;

    try {
      const { data, error } = await supabase.rpc(
        'get_total_unread_messages',
        { p_user_id: userProfile.id }
      );

      if (error) throw error;

      if (mountedRef.current) {
        setUnreadCount(data || 0);
      }
    } catch (error) {
      console.error('[ChatBadge] Error fetching unread count:', error);
    }
  }, [userProfile?.id]);

  /**
   * Debounced fetch to prevent rapid refetches from realtime events
   */
  const debouncedFetch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchUnreadCount();
    }, DEBOUNCE_DELAY);
  }, [fetchUnreadCount]);

  // Initial fetch
  useEffect(() => {
    mountedRef.current = true;
    fetchUnreadCount();

    return () => {
      mountedRef.current = false;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fetchUnreadCount]);

  // Realtime subscription with debouncing
  useEffect(() => {
    if (!userProfile?.id) return;

    let channel = null;

    const setupSubscription = async () => {
      // Set auth token for private channel
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        await supabase.realtime.setAuth(session.access_token);
      }

      // Subscribe to broadcast channel
      channel = supabase
        .channel(`chat:${userProfile.id}`, {
          config: { private: true }
        })
        .on('broadcast', { event: 'UPDATE' }, () => {
          // Debounced refetch on any conversation update
          debouncedFetch();
        })
        .on('broadcast', { event: 'INSERT' }, () => {
          // Debounced refetch when user joins a conversation
          debouncedFetch();
        })
        .on('broadcast', { event: 'DELETE' }, () => {
          // Debounced refetch when user leaves a conversation
          debouncedFetch();
        })
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            console.log('[ChatBadge] Subscribed to broadcast channel');
          }
        });
    };

    setupSubscription();

    return () => {
      if (channel) {
        channel.unsubscribe();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [userProfile?.id, debouncedFetch]);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="relative h-9 w-9"
      asChild
      title="Chat"
    >
      <Link to="/chat">
        <MessageCircle className="h-5 w-5 scale-x-[-1]" />
        {unreadCount > 0 && (
          <Badge
            variant="destructive"
            className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </Link>
    </Button>
  );
};
